import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { trigger, style, transition, animate, query, stagger } from '@angular/animations';
import { Router } from '@angular/router';
import { BudgetMapperService } from '../services/budget-mapper.service';
import { Budget, BudgetCalculationItem } from '../models/budget-response.interface';

@Component({
  selector: 'nh-budget-calculation',
  templateUrl: './budget-calculation.component.html',
  styleUrls: ['./budget-calculation.component.scss'],
  standalone: false,
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: '0', overflow: 'hidden' }),
        animate('400ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: '1' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: '1', overflow: 'hidden' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: '0' }))
      ])
    ]),
    trigger('fadeInStagger', [
      transition('* => *', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateX(-10px)' }),
          stagger('50ms', [
            animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateX(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class BudgetCalculationComponent implements OnInit, OnChanges {
  @Input() memberName: string = '';
  @Input() budgetData: Budget | null = null;
  @Output() onNavigateToSubscreen = new EventEmitter<void>();

  budgetType: string = '';
  incomePanelExpanded: boolean = true;
  ssiFbrPanelExpanded: boolean = true;
  resourcesPanelExpanded: boolean = true;
  // Track expanded state for each item
  expandedItems: { [key: string]: boolean } = {};
  // Data arrays for different sections
  incomeItems: BudgetCalculationItem[] = [];
  ssiFbrItems: BudgetCalculationItem[] = [];
  resourceItems: BudgetCalculationItem[] = [];

  constructor(
    private budgetMapperService: BudgetMapperService,
    private router: Router
  ) { }

  ngOnInit(): void {
    //Clear any previously stored subscreen data to prevent prepopulation, when navigating back to budget details via logo or other routes
    sessionStorage.removeItem('budgetSubScreenData');
    this.processBudgetData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budgetData']) {
      this.processBudgetData();
    }
  }

  
  // Process budget data and map to UI display format
  private processBudgetData(): void {
    if (!this.budgetData) {
      this.resetData();
      return;
    }

    this.budgetType = this.budgetData.type || '';
    this.mapAllBudgetSections();
  }

  private mapAllBudgetSections(): void {
    // Map income items
    this.incomeItems = this.budgetMapperService.mapIncomeItems(this.budgetData!);
    // Map nested budget sections (only if valid data exists)
    this.ssiFbrItems = this.shouldShowSsiFbrSection() ? this.budgetMapperService.mapSsiFbrIncomeTest(this.budgetData!) : [];
    this.resourceItems = this.shouldShowResourcesSection() ? this.budgetMapperService.mapResourceItems(this.budgetData!) : [];
  }

  
  //Check if SSI/FBR section should be displayed
  private shouldShowSsiFbrSection(): boolean {
    return !!this.budgetData?.ssiFbrIncomeTest &&
      this.hasValidSsiFbrData(this.budgetData.ssiFbrIncomeTest);
  }

  
  //Check if Resources section should be displayed
  private shouldShowResourcesSection(): boolean {
    return !!this.budgetData?.resources;
  }

  /**
   * Validate SSI/FBR data has at least one non-null field
   * @param ssiFbrData - SSI/FBR income test data
   * @returns true if valid data exists, false otherwise
   */
  private hasValidSsiFbrData(ssiFbrData: any): boolean {
    if (!ssiFbrData) return false;

    const requiredFields = [
      ssiFbrData?.netCountableUnearnedIncomeTotal,
      ssiFbrData?.irregularUnearnedIncomeDisregard,
      ssiFbrData?.ineligibleSpousesDeemedUnearnedIncome,
      ssiFbrData?.ineligibleSpousesDeemedEarnedIncome,
      ssiFbrData?.ineligibleParentsDeemedIncome,
      ssiFbrData?.generalDeduction,
      ssiFbrData?.childSupportDisregard,
      ssiFbrData?.totalNetUnearnedIncome,
      ssiFbrData?.ssiFbrLimit,
      ssiFbrData?.ssiFbrResult
    ];

    return requiredFields.some(field => field !== null && field !== undefined);
  }
  
  //Reset all data arrays
  private resetData(): void {
    this.budgetType = '';
    this.incomeItems = [];
    this.ssiFbrItems = [];
    this.resourceItems = [];
  }

  toggleItem(itemId: string): void {
    this.expandedItems[itemId] = !this.expandedItems[itemId];
  }

  isExpanded(itemId: string): boolean {
    return this.expandedItems[itemId] || false;
  }

  getInfoTooltip(item: any): string {
    return item?.tooltipText;
  }

  
  //Get subheader text based on budget type
  getSubheaderText(): string {
    if (this.budgetData?.coe === 'K04' || this.budgetData?.coe === 'K01' || this.budgetData?.coe === 'K03' || this.budgetData?.coe === 'K02') {
      return 'KB_BUDGET_HEADER';
    } else {
      if (this.budgetType === 'MAGI' || this.budgetType === 'CK') {
        return 'MAGI_BUDGET_DETAILS_SUBHEADER';
      } else if (this.budgetType === 'ABD') {
        return 'ABD_BUDGET_DETAILS_SUBHEADER';
      }
      return 'MAGI_BUDGET_DETAILS_SUBHEADER';
    }
  }

  loadSubscreen(item: BudgetCalculationItem): void {
    this.onNavigateToSubscreen.emit()
    const getNodDetails = sessionStorage.getItem('nodBudgetPayload');
    const subScreenData = {
      indvId: item?.indivId,
      coe: item?.coe,
      subScreenType: item?.subScreenType,
      coReqSeq: getNodDetails ? JSON.parse(getNodDetails).coReqSeq : undefined,
      budgetType: this.budgetType,
    };
    sessionStorage.setItem('budgetSubScreenData', JSON.stringify(subScreenData));
    this.router.navigate(['/user-profile/budget-details/', item?.subScreenType]);
  }
}